import { getFirebase } from 'firestorter';
import moment from 'moment-timezone';
import { DateFormat } from './date';

const TIME_INCREMENT_MINUTES = 5;

/**
 * Formats a Date object text for chat
 * @param {Date} date
 * @param {Boolean} exact show minutes and hours instead of "Today" and "Yesterday"
 */
const formatDateForChat = (date, exact = false) => {
  if (!date) {
    return '';
  }

  if (exact) {
    return moment(date).fromNow();
  }
  return moment(date).calendar(null, {
    sameDay: '[Today]',
    lastDay: '[Yesterday]',
    lastWeek: `[Last] dddd (${DateFormat.DEFAULT_DATE_FORMAT})`,
    sameElse: (now) => (
      `[${moment(date).from(now)}] (${DateFormat.DEFAULT_DATE_FORMAT})`
    ),
  });
};

/**
 * This simple function adds zeros to the left, if needed. We prepend two zeros to the number that's passed as param
 * and then we take the last two digits.
 *
 * @example
 *   digits = 7;
 *   padded string = '007';
 *   result: '07';
 *
 * @param {number} digits The digits to add the zeros to.
 * @returns {string} The padded string
 */
const leftZeroPadding = (digits) => `00${digits}`.slice(-2);

/**
 * Parse a time value expressed in milliseconds and convert it to a time string in the following format: hh:mm:ss.
 * The hours portion will not be returned if the converted time is less or equal to 59:59.
 *
 * @param {number} timeInMillis The time in milliseconds
 * @returns {string} The converted time
 */
const parseTimeInMillis = (timeInMillis) => {
  const duration = moment.duration(timeInMillis);
  const seconds = duration.seconds();
  const minutes = duration.minutes();
  const hours = duration.hours();

  let timeString = `${leftZeroPadding(minutes)}:${leftZeroPadding(seconds)}`;

  if (hours) {
    timeString = `${leftZeroPadding(hours)}:${timeString}`;
  }

  return timeString;
};

/**
 * Extracts time from full date.
 * @param {Date} date
 */
const formatTimeForChatMessage = (date) => (
  !date
    ? ''
    : moment(date).format('h:mm A')
);

/**
 * Checks if two given dates are the same day.
 * @param {Date} dateA
 * @param {Date} dateB
 */
const areDatesTheSameDay = (dateA, dateB) => (
  moment(dateA).isSame(dateB, 'day')
);

/**
 * Rounds the given moment object to the next 5-minute increment.
 *
 * @param {object} time - The moment object representing the time to be rounded.
 * @returns {object} - A new moment object rounded to the next 5-minute increment.
 */
const roundToNext5Minutes = (time) => {
  const minutes = time.minute();
  const roundedMinutes = Math.ceil((minutes + 1) / TIME_INCREMENT_MINUTES) * TIME_INCREMENT_MINUTES;
  return time.clone().minute(roundedMinutes).second(0);
};

/**
 * Gets the UTC offset for a given timezone.
 *
 * @param {string} timezone - The timezone identifier (e.g., 'America/New_York').
 * @returns {number} - The UTC offset in minutes.
 */
const getUTCOffset = (timezone) => moment.tz(timezone).utcOffset();

/**
 * Returns a Firestore server timestamp FieldValue.
 *
 * @returns {FieldValue}.
 */
const getServerTimestamp = () => getFirebase().default.firestore.FieldValue.serverTimestamp();

export {
  formatDateForChat,
  parseTimeInMillis,
  formatTimeForChatMessage,
  areDatesTheSameDay,
  roundToNext5Minutes,
  getUTCOffset,
  getServerTimestamp,
};
